import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { financeApi, Grant } from '../../api/finance';
import { masterDataApi } from '../../api/masterData';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Lock, PauseCircle, RotateCcw, Eye } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export function GrantsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const grantsQuery = useQuery({ queryKey: ['finance-grants'], queryFn: () => financeApi.listGrants() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: showForm });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: showForm });

  const { register, handleSubmit, reset, watch, setValue } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-grants'] });

  // Real project-derived values - once a project is selected, its own
  // donor and currency are the source of truth, never a second manual
  // entry that could drift from what the project record actually says.
  const selectedProjectId = watch('projectId');
  const selectedProject = projectsQuery.data?.data.find((p) => String(p.id) === selectedProjectId);
  const derivedDonor = selectedProject ? donorsQuery.data?.data.find((d) => d.id === selectedProject.donor_id) : undefined;

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    setValue('projectId', projectId);
    const project = projectsQuery.data?.data.find((p) => String(p.id) === projectId);
    if (project) {
      setValue('donorId', String(project.donor_id));
      setValue('currencyCode', project.currency_code);
    }
  };

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createGrant({
      grantName: v.grantName, donorId: Number(v.donorId), projectId: v.projectId ? Number(v.projectId) : undefined,
      grantNumber: v.grantNumber || undefined, startDate: v.startDate, endDate: v.endDate,
      totalAwardAmount: v.totalAwardAmount ? Number(v.totalAwardAmount) : undefined, currencyCode: v.currencyCode || 'NGN',
    }),
    onSuccess: (result) => { showSuccess(`Grant created - ${result.data.grantReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create grant.'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'closed' | 'suspended' }) => financeApi.setGrantStatus(id, status),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <PageHeader title="Grants" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Create Grant'}</button>} />

      {showForm && (
        <section className="card">
          <h2>New Grant</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Grant Name<input {...register('grantName', { required: true })} /></label>
            <label>
              Project (optional)
              <select value={selectedProjectId ?? ''} onChange={handleProjectChange}>
                <option value="">- none -</option>
                {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </label>
            <input type="hidden" {...register('projectId')} />

            {selectedProject ? (
              <>
                <p className="muted">Donor and currency below are fetched directly from the selected project and cannot be overridden here.</p>
                <label>Donor<input value={derivedDonor?.name ?? `Donor #${selectedProject.donor_id}`} disabled /></label>
                <label>Currency<input value={selectedProject.currency_code} disabled /></label>
                <input type="hidden" {...register('donorId')} />
                <input type="hidden" {...register('currencyCode')} />
              </>
            ) : (
              <>
                <label>
                  Donor
                  <select {...register('donorId', { required: true })}>
                    <option value="">- select -</option>
                    {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </label>
                <label>Currency<input {...register('currencyCode')} placeholder="NGN" /></label>
              </>
            )}

            <label>Grant Number<input {...register('grantNumber')} /></label>
            <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
            <label>End Date<input type="date" {...register('endDate', { required: true })} /></label>
            <label>Total Award Amount<input type="number" {...register('totalAwardAmount')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Grants" />
        <div className="panel-body">
        {grantsQuery.data && (
          <DataTable<Grant>
            rows={grantsQuery.data.data}
            rowKey={(g) => g.id}
            emptyMessage="No grants yet."
            columns={[
              { header: 'Reference', render: (g) => g.grant_reference },
              { header: 'Name', render: (g) => g.grant_name },
              { header: 'Donor', render: (g) => g.donor_name ?? '-' },
              { header: 'Project', render: (g) => g.project_name ?? '-' },
              { header: 'Period', render: (g) => `${formatDate(g.start_date)} - ${formatDate(g.end_date)}` },
              { header: 'Status', render: (g) => <StatusBadge status={g.status} /> },
              {
                header: '',
                render: (g) => (
                  <span className="icon-action-group">
                    <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/finance/grants/${g.id}`)} />
                    {g.status === 'active' && <IconActionButton icon={Lock} label="Close" onClick={() => toggleStatusMutation.mutate({ id: g.id, status: 'closed' })} />}
                    {g.status === 'active' && <IconActionButton icon={PauseCircle} label="Suspend" onClick={() => toggleStatusMutation.mutate({ id: g.id, status: 'suspended' })} />}
                    {g.status !== 'active' && <IconActionButton icon={RotateCcw} label="Reactivate" onClick={() => toggleStatusMutation.mutate({ id: g.id, status: 'active' })} />}
                  </span>
                ),
              },
            ]}
          />
        )}
        </div>
      </div>
    </div>
  );
}
